import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import SocketService from '../../../../packages/shared/socket';
import { availableScreens } from '../../../../packages/shared/useScreenNavigation';

const TitleContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1000;
`;

const TitleButton = styled.button`
  background: none;
  border: none;
  color: white;
  margin: 0;
  padding: 0;
  font-size: 1.2rem;
  font-weight: ${props => props.weight || '400'};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:focus {
    outline: none;
  }
`;

const ArrowIcon = styled.span`
  display: inline-block;
  font-size: 0.7em;
  transition: transform 0.2s ease;
  transform: ${props => props.open ? 'rotate(180deg)' : 'rotate(0)'};
  opacity: 0.6;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 10px;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  min-width: 280px;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  z-index: 10000;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.95rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  
  &:focus {
    outline: none;
  }
`;

const MenuIcon = styled.span`
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
`;

const MenuText = styled.div`
  flex: 1;
`;

const MenuName = styled.div`
  font-weight: 500;
  margin-bottom: 2px;
`;

const MenuDescription = styled.div`
  font-size: 0.75rem;
  opacity: 0.7;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 8px 0;
`;

function ScreenTitle({ children, size = 'large', weight, align }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    // Get the current screen based on route
    const current = availableScreens.find(screen => 
      location.pathname === screen.route
    );
    console.log('🔍 ScreenTitle: Current route:', location.pathname);
    console.log('🔍 ScreenTitle: Found screen:', current);
    setCurrentScreen(current);
  }, [location]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleScreenSelect = (screen) => {
    setIsOpen(false);
    
    // Don't do anything if selecting the current screen
    if (screen.route === location.pathname) return;
    
    // Navigate to the new screen
    navigate(screen.route);
    
    // Notify facilitator through socket
    const screenId = localStorage.getItem('pairedScreenId');
    if (SocketService.socket && screenId) {
      console.log('🔄 Screen change by user - notifying facilitator');
      
      // Send screen state update
      SocketService.socket.emit('screen-state-update', {
        screenId: screenId,
        type: screen.id,
        stats: {
          screenType: screen.name,
          icon: screen.icon,
          userChanged: true,
          timestamp: new Date().toISOString()
        },
        customPreview: {
          message: `User manually switched to ${screen.name}`,
          icon: screen.icon
        }
      });
      
      // Also send a screen-changed event
      SocketService.socket.emit('screen-changed', {
        screenId: screenId,
        previousScreen: currentScreen?.id,
        newScreen: screen.id,
        newScreenName: screen.name,
        changedBy: 'user',
        timestamp: new Date().toISOString()
      });
    }
  };

  // Don't show dropdown on facilitator view or setup screens
  const isSpecialScreen = ['/facilitator', '/display-setup', '/control'].includes(location.pathname);
  
  if (isSpecialScreen) {
    // Return a simple title without dropdown functionality
    return (
      <TitleContainer>
        <h1 style={{ 
          color: 'white', 
          margin: 0, 
          fontSize: size === 'large' ? '2rem' : '1.5rem',
          fontWeight: weight || '300',
          textAlign: align || 'center'
        }}>
          {children}
        </h1>
      </TitleContainer>
    );
  }

  return (
    <TitleContainer>
      <TitleButton
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        size={size}
        weight={weight}
        align={align}
      >
        {children || currentScreen?.name || 'Select Screen'}
        <ArrowIcon open={isOpen}>▼</ArrowIcon>
      </TitleButton>
      
      {isOpen && (
        <DropdownMenu ref={dropdownRef}>
          {availableScreens.map((screen, index) => (
            <React.Fragment key={screen.id}>
              {index > 0 && index % 4 === 0 && <Divider />}
              <MenuItem
                active={screen.route === location.pathname}
                onClick={() => handleScreenSelect(screen)}
              >
                <MenuIcon>{screen.icon}</MenuIcon>
                <MenuText>
                  <MenuName>{screen.name}</MenuName>
                  <MenuDescription>{screen.description}</MenuDescription>
                </MenuText>
              </MenuItem>
            </React.Fragment>
          ))}
        </DropdownMenu>
      )}
    </TitleContainer>
  );
}

export default ScreenTitle;